/**
 * Taban bileşenler. Renk yok — yalnızca jeton sınıfları (`pf-*`, `text-muted`
 * gibi tema değişkenine bağlı Tailwind sınıfları) kullanılır.
 */

import { useId } from "react";
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import * as Label from "@radix-ui/react-label";
import { M } from "../dil";
import { errorDetail, errorText } from "../lib/rpcError";

export function cx(...parts: readonly (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

// ---------------------------------------------------------------------------
// Düğme
// ---------------------------------------------------------------------------

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: "primary" | "ghost" | "danger";
}

export function Button({ variant = "ghost", className, type = "button", ...rest }: ButtonProps) {
  const variantClass =
    variant === "primary" ? "pf-btn-primary" : variant === "danger" ? "pf-btn-danger" : "pf-btn-ghost";
  return <button type={type} className={cx("pf-btn", variantClass, className)} {...rest} />;
}

// ---------------------------------------------------------------------------
// Form alanı sarmalayıcısı — etiket her zaman gerçek <label>
// ---------------------------------------------------------------------------

export interface FieldProps {
  readonly label: string;
  readonly htmlFor?: string;
  readonly hint?: string;
  readonly error?: string;
  readonly required?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
}

export function Field({ label, htmlFor, hint, error, required, className, children }: FieldProps) {
  return (
    <div className={cx("flex flex-col gap-1", className)}>
      <Label.Root htmlFor={htmlFor} className="text-[12px] font-semibold text-muted">
        {label}
        {required === true && (
          <span aria-hidden="true" className="text-danger">
            {" *"}
          </span>
        )}
      </Label.Root>
      {children}
      {error !== undefined && error !== "" ? (
        <p className="text-[12px] text-danger">{error}</p>
      ) : hint !== undefined && hint !== "" ? (
        <p className="text-[12px] text-faint">{hint}</p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Girdiler
// ---------------------------------------------------------------------------

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Para/miktar alanı: sabit genişlikli rakam ve sağa hizalı. */
  readonly numeric?: boolean;
}

export function TextInput({ numeric = false, className, ...rest }: TextInputProps) {
  return (
    <input
      type="text"
      className={cx("pf-input", numeric && "pf-num", className)}
      autoComplete="off"
      spellCheck={false}
      {...rest}
    />
  );
}

export function DateInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input type="date" className={cx("pf-input pf-num", className)} {...rest} />;
}

export function TextArea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx("pf-input", className)} {...rest} />;
}

/**
 * Yerel <select>. Radix Select yerine bunu kullanıyoruz: yoğun bir kayıt
 * formunda klavye ve mobil davranışı yerel denetimde daha iyi ve erişilebilirlik
 * ücretsiz geliyor. Radix; diyalog, onay ve anahtar için kullanılıyor.
 */
export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly placeholder?: string;
}

export function Select({ options, placeholder, className, ...rest }: SelectProps) {
  return (
    <select className={cx("pf-input", className)} {...rest}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Durum bileşenleri: yükleniyor / boş / hata — her ekranda üçü de tasarlı
// ---------------------------------------------------------------------------

export function Spinner({ label = M.ui.yukleniyor }: { readonly label?: string }) {
  return (
    <span role="status" aria-live="polite" className="inline-flex items-center gap-2 text-muted">
      <span
        aria-hidden="true"
        className="inline-block size-3 animate-spin rounded-full border-2 border-line-strong border-t-accent"
      />
      <span className="text-[12px]">{label}</span>
    </span>
  );
}

export function LoadingBlock({ label = M.ortak.yukleniyor }: { readonly label?: string }) {
  return (
    <div className="flex items-center justify-center px-4 py-10">
      <Spinner label={label} />
    </div>
  );
}

export interface EmptyStateProps {
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
      <p className="text-[14px] font-semibold text-ink">{title}</p>
      {description !== undefined && <p className="max-w-md text-[13px] text-muted">{description}</p>}
      {action !== undefined && <div className="mt-2">{action}</div>}
    </div>
  );
}

export interface ErrorBlockProps {
  readonly error: unknown;
  readonly onRetry?: () => void;
  readonly title?: string;
}

export function ErrorBlock({ error, onRetry, title = M.ui.veriOkunamadi }: ErrorBlockProps) {
  const detail = errorDetail(error);
  return (
    <div
      role="alert"
      className="m-4 rounded-pf border border-danger-border bg-danger-soft p-3 text-[13px] text-ink"
    >
      <p className="font-semibold text-danger">{title}</p>
      <p className="mt-1">{errorText(error)}</p>
      {detail !== undefined && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[12px] text-muted">{M.ortak.teknikAyrinti}</summary>
          <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap text-[11px] text-muted">{detail}</pre>
        </details>
      )}
      {onRetry !== undefined && (
        <div className="mt-2">
          <Button onClick={onRetry}>{M.ortak.yenidenDene}</Button>
        </div>
      )}
    </div>
  );
}

/** Form içi hata satırı. */
export function InlineError({ error }: { readonly error: unknown }) {
  if (error === undefined || error === null) return null;
  return (
    <p role="alert" className="text-[12px] text-danger">
      {errorText(error)}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Uyarı kutusu
// ---------------------------------------------------------------------------

export interface NoticeProps {
  readonly tone: "warn" | "info" | "danger";
  readonly title: string;
  readonly children?: ReactNode;
}

export function Notice({ tone, title, children }: NoticeProps) {
  const toneClass =
    tone === "warn"
      ? "border-warn-border bg-warn-soft"
      : tone === "danger"
        ? "border-danger-border bg-danger-soft"
        : "border-accent-border bg-accent-soft";
  const titleClass = tone === "warn" ? "text-warn" : tone === "danger" ? "text-danger" : "text-accent";
  return (
    <div role="note" className={cx("rounded-pf border p-3 text-[13px] text-ink", toneClass)}>
      <p className={cx("font-semibold", titleClass)}>{title}</p>
      {children !== undefined && <div className="mt-1 space-y-1 leading-relaxed">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sayı hücresi
// ---------------------------------------------------------------------------

export interface NumberCellProps {
  readonly children: ReactNode;
  /** Kazanç/kayıp renklendirmesi. Aşama 1'de yalnızca miktar işareti için. */
  readonly sign?: -1 | 0 | 1;
  readonly currency?: string;
  readonly className?: string;
}

export function NumberCell({ children, sign, currency, className }: NumberCellProps) {
  const signClass = sign === undefined ? "" : sign > 0 ? "text-gain" : sign < 0 ? "text-loss" : "text-flat";
  return (
    <span className={cx("pf-num inline-flex items-baseline justify-end gap-1", signClass, className)}>
      <span>{children}</span>
      {currency !== undefined && <span className="text-[11px] font-normal text-faint">{currency}</span>}
    </span>
  );
}

export function Tag({ children, className }: { readonly children: ReactNode; readonly className?: string }) {
  return <span className={cx("pf-tag", className)}>{children}</span>;
}

/** Başlık + araç çubuğu düzeni; tüm ekranlar aynı iskeleti kullanır. */
export interface ScreenProps {
  readonly title: string;
  readonly description?: string;
  readonly toolbar?: ReactNode;
  readonly children: ReactNode;
}

export function Screen({ title, description, toolbar, children }: ScreenProps) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="flex min-h-0 flex-1 flex-col">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-line px-4 py-3">
        <div>
          <h1 id={headingId} className="text-[16px] font-semibold tracking-tight text-ink">
            {title}
          </h1>
          {description !== undefined && <p className="mt-0.5 text-[12px] text-muted">{description}</p>}
        </div>
        {toolbar !== undefined && <div className="flex flex-wrap items-center gap-2">{toolbar}</div>}
      </header>
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
    </section>
  );
}
